import React, { useState } from 'react';
import { useI18n } from '../i18n';

const DC_CONNECTORS = [
  { id: 'chademo', name: 'CHAdeMO' },
  { id: 'ccs2', name: 'CCS Combo 2 (CCS2)' },
  { id: 'tesla', name: 'Tesla Supercharger' },
];

const AC_CONNECTORS = [
  { id: 'type2', name: 'Type 2 (Mennekes)' },
  { id: 'type1', name: 'Type 1 (J1772)' },
  { id: 'schuko', name: 'Schuko' },
];

const POWER_RANGES = [
  { id: 'lte22', label: '≤ 22 kW', min: 0, max: 22 },
  { id: 'gt22lt50', label: '> 22 kW y < 50 kW', min: 22.1, max: 49.9 },
  { id: 'eq50', label: '= 50 kW', min: 50, max: 50 },
  { id: 'gt50lt100', label: '> 50 kW y < 100 kW', min: 50.1, max: 99.9 },
  { id: 'gte100lt150', label: '≥ 100 kW y < 150 kW', min: 100, max: 149.9 },
  { id: 'gt150lt250', label: '> 150 kW y < 250 kW', min: 150.1, max: 249.9 },
  { id: 'gte250', label: '≥ 250 kW', min: 250, max: Infinity },
];

export default function EVFilters({ filters, onFiltersChange, totalChargers }) {
  const { t } = useI18n();
  const [expandedSections, setExpandedSections] = useState({
    currentType: true,
    connectors: true,
    power: false,
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleCurrentTypeChange = (type) => {
    const newCurrentTypes = filters.currentTypes.includes(type)
      ? filters.currentTypes.filter(t => t !== type)
      : [...filters.currentTypes, type];
    
    // When unchecking a current type, also remove its connectors
    let newConnectors = [...filters.connectors];
    if (!newCurrentTypes.includes(type)) {
      const connectorsToRemove = type === 'DC' 
        ? DC_CONNECTORS.map(c => c.id) 
        : AC_CONNECTORS.map(c => c.id);
      newConnectors = newConnectors.filter(c => !connectorsToRemove.includes(c));
    }
    
    onFiltersChange({ 
      ...filters, 
      currentTypes: newCurrentTypes,
      connectors: newConnectors,
    });
  };

  const handleConnectorChange = (connectorId) => {
    const newConnectors = filters.connectors.includes(connectorId)
      ? filters.connectors.filter(c => c !== connectorId)
      : [...filters.connectors, connectorId];
    onFiltersChange({ ...filters, connectors: newConnectors });
  };

  const handlePowerRangeChange = (rangeId) => {
    const newRanges = filters.powerRanges.includes(rangeId)
      ? filters.powerRanges.filter(r => r !== rangeId)
      : [...filters.powerRanges, rangeId];
    onFiltersChange({ ...filters, powerRanges: newRanges });
  };

  // Show connectors based on selected current types
  const availableConnectors = [
    ...(filters.currentTypes.includes('DC') ? DC_CONNECTORS : []),
    ...(filters.currentTypes.includes('AC') ? AC_CONNECTORS : []),
  ];

  // Clear all filters
  const handleClearFilters = () => {
    onFiltersChange({ currentTypes: [], connectors: [], powerRanges: [] });
  };

  // Count active filters
  const activeFilterCount = 
    filters.currentTypes.length + 
    filters.connectors.length + 
    filters.powerRanges.length;

  return (
    <div className="ev-filters">
      <div className="ev-filters-header">
        <h3>
          {t("filters.title")}
          {activeFilterCount > 0 && (
            <span className="filter-count">{activeFilterCount}</span>
          )}
        </h3>
        <div className="ev-filters-actions">
          {totalChargers != null && (
            <span className="total-chargers-badge">
              ⚡ {t("filters.totalChargers", { count: totalChargers })}
            </span>
          )}
          {activeFilterCount > 0 && (
            <button
              type="button"
              className="clear-filters-btn"
              onClick={handleClearFilters}
            >
              ✕ {t("filters.clear")}
            </button>
          )}
        </div>
      </div>
      
      <div className="ev-filters-content">
        {/* Current Type Section */}
        <div className="filter-section">
          <button 
            type="button"
            className="section-toggle"
            onClick={() => toggleSection('currentType')}
          >
            <span>{t("filters.currentType")}</span>
            <span className="toggle-icon">{expandedSections.currentType ? '▼' : '▶'}</span>
          </button>
          {expandedSections.currentType && (
            <div className="filter-options horizontal">
              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.currentTypes.includes('DC')} 
                  onChange={() => handleCurrentTypeChange('DC')} 
                />
                <span>{t("filters.dc")}</span>
              </label>
              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.currentTypes.includes('AC')} 
                  onChange={() => handleCurrentTypeChange('AC')} 
                />
                <span>{t("filters.ac")}</span>
              </label>
            </div>
          )}
        </div>

        {/* Connectors Section */}
        {availableConnectors.length > 0 && (
          <div className="filter-section">
            <button 
              type="button"
              className="section-toggle"
              onClick={() => toggleSection('connectors')}
            >
              <span>{t("filters.connectors")}</span>
              <span className="toggle-icon">{expandedSections.connectors ? '▼' : '▶'}</span>
            </button>
            {expandedSections.connectors && (
              <div className="filter-options">
                {filters.currentTypes.includes('DC') && (
                  <div className="connector-group">
                    <span className="group-label">DC:</span>
                    <div className="group-options">
                      {DC_CONNECTORS.map(conn => (
                        <label key={conn.id} className="filter-checkbox">
                          <input 
                            type="checkbox" 
                            checked={filters.connectors.includes(conn.id)} 
                            onChange={() => handleConnectorChange(conn.id)} 
                          />
                          <span>{conn.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {filters.currentTypes.includes('AC') && (
                  <div className="connector-group">
                    <span className="group-label">AC:</span>
                    <div className="group-options">
                      {AC_CONNECTORS.map(conn => (
                        <label key={conn.id} className="filter-checkbox">
                          <input 
                            type="checkbox" 
                            checked={filters.connectors.includes(conn.id)} 
                            onChange={() => handleConnectorChange(conn.id)} 
                          />
                          <span>{conn.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Power Range Section */}
        <div className="filter-section">
          <button 
            type="button"
            className="section-toggle"
            onClick={() => toggleSection('power')}
          >
            <span>{t("filters.power")}</span>
            <span className="toggle-icon">{expandedSections.power ? '▼' : '▶'}</span>
          </button>
          {expandedSections.power && (
            <div className="filter-options power-grid">
              {POWER_RANGES.map(range => (
                <label key={range.id} className="filter-checkbox">
                  <input 
                    type="checkbox" 
                    checked={filters.powerRanges.includes(range.id)} 
                    onChange={() => handlePowerRangeChange(range.id)} 
                  />
                  <span>{range.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Export constants for use elsewhere
export { DC_CONNECTORS, AC_CONNECTORS, POWER_RANGES };
